import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase';

export interface CRUDOptions {
  orderByField?: string;
  orderDirection?: 'asc' | 'desc';
}

export const crud = <T extends Record<string, any>>(collectionName: string, options: CRUDOptions = {}) => ({
  getAll: async (): Promise<(T & { id: string; createdAt: Date; updatedAt: Date })[]> => {
    try {
      const collectionRef = collection(db, collectionName);
      let q = query(collectionRef);
      
      if (options.orderByField) {
        q = query(collectionRef, orderBy(options.orderByField, options.orderDirection || 'asc'));
      }
      
      const snapshot = await getDocs(q);
      
      const results = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data() as T,
        createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
        updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt,
      }));
      
      return results;
    } catch (error) {
      console.error(`❌ Error fetching ${collectionName}:`, error);
      throw error;
    }
  },

  create: async (data: Omit<T, 'id' | 'createdAt' | 'updatedAt'>): Promise<T & { id: string; createdAt: Date; updatedAt: Date }> => {
    try {
      const now = Timestamp.now();
      const docRef = await addDoc(collection(db, collectionName), {
        ...data,
        createdAt: now,
        updatedAt: now,
      });
      return { id: docRef.id, ...data as T, createdAt: now.toDate(), updatedAt: now.toDate() };
    } catch (error) {
      console.error(`Error creating ${collectionName}:`, error);
      throw error;
    }
  },

  update: async (id: string, data: Partial<T>): Promise<T & { id: string; updatedAt: Date }> => {
    try {
      const docRef = doc(db, collectionName, id);
      const updateData = {
        ...data,
        updatedAt: Timestamp.now(),
      };
      await updateDoc(docRef, updateData);
      return { id, ...data as T, updatedAt: updateData.updatedAt.toDate() };
    } catch (error) {
      console.error(`Error updating ${collectionName}:`, error);
      throw error;
    }
  },

  delete: async (id: string): Promise<string> => {
    try {
      const docRef = doc(db, collectionName, id);
      await deleteDoc(docRef);
      return id;
    } catch (error) {
      console.error(`Error deleting ${collectionName}:`, error);
      throw error;
    }
  },
});
